import { NAME_MAX_LENGTH } from "./constants.js";

/**
 * Names that must never appear in curated dog-name lists (case-insensitive).
 * Kept as an explicit denylist so regenerations cannot reintroduce them.
 */
export const BANNED_DOG_NAMES = ["Ginger", "Ginge"] as const;

const BANNED_KEYS = new Set(BANNED_DOG_NAMES.map((n) => n.toLowerCase()));

/** Cozy pup nicknames + everyday human first names for guest quick play. */
const RAW_DOG_NAMES = [
  // --- Cozy / snacky / park pup ---
  "Acorn", "Aioli", "Almond", "Amber", "Angel", "Anise", "Apricot", "Bagel",
  "Bailey", "Bambino", "Bandit", "Barkley", "Basil", "Bean", "Beignet", "Binky",
  "Biscotti", "Biscuit", "Bluebell", "Blueberry", "Boba", "Bonnie", "Boop", "Boots",
  "Bramble", "Breeze", "Briar", "Brownie", "Bubbles", "Buddy", "Bumble", "Bun",
  "Butter", "Buttercup", "Butterscotch", "Button", "Cacao", "Candy", "Caramel",
  "Cedar", "Chai", "Cheddar", "Cherry", "Chestnut", "Chickadee", "Chickpea", "Chip",
  "Churro", "Cinnamon", "Clover", "Cocoa", "Coconut", "Cookie", "Cosmo", "Cotton",
  "Cricket", "Crumb", "Cuddle", "Cupcake", "Cuppa", "Custard", "Daffodil", "Daisy",
  "Dandelion", "Dewdrop", "Dinky", "Doodle", "Dottie", "Dove", "Dream", "Dumpling",
  "Dusty", "Eddy", "Ellie", "Fable", "Fennel", "Fig", "Finn", "Fizz", "Flannel",
  "Fleece", "Fluff", "Fluffy", "Fudge", "Gizmo", "Glow", "Gumdrop", "Gummy", "Hazel",
  "Heart", "Hershey", "Honey", "Honeybee", "Huckle", "Huggy", "Indie", "Ivy", "Jam",
  "Jammie", "Jelly", "Jellybean", "Juniper", "Juno", "Kisses", "Kit", "Kiwi", "Koda",
  "Latte", "Lavender", "Lemon", "Lemonade", "Lilac", "Lily", "Lollipop", "Lucky",
  "Lulu", "Macaron", "Mango", "Maple", "Marigold", "Marshmallow", "Marzipan", "Meadow",
  "Mellow", "Meringue", "Milo", "Miso", "Mocha", "Mochi", "Muffin", "Munchkin",
  "Nectar", "Nibble", "Nibbles", "Nimbus", "Noodle", "Nugget", "Nutmeg", "Oats",
  "Olive", "Oreo", "Otter", "Pancake", "Paprika", "Patch", "Peaches", "Peanut",
  "Pebble", "Pepper", "Periwinkle", "Pesto", "Petal", "Pickle", "Pip", "Pippin",
  "Pixel", "Plum", "Pogo", "Pompom", "Popcorn", "Poppy", "Puddin", "Pudding",
  "Puddles", "Puff", "Pumpkin", "Pupper", "Puppy", "Quokka", "Raindrop", "Raisin",
  "Rascal", "Remy", "Ribbon", "Rio", "Rolo", "Roo", "Rosie", "Rory", "Sage", "Scone",
  "Scooter", "Scout", "Sherbet", "Shortcake", "Skipper", "Sky", "S'mores",
  "Snickerdoodle", "Sniffles", "Snowball", "Snuggle", "Socks", "Softie", "Sorbet",
  "Sparkle", "Speckles", "Splash", "Sprout", "Sprinkles", "Starlight", "Sugar",
  "Sugarplum", "Sundae", "Sunny", "Sweetpea", "Taffy", "Tater", "Teacup", "Teddy",
  "Thumper", "Thyme", "Tink", "Tippy", "Toast", "Toby", "Tofu", "Toffee", "Treasure",
  "Truffle", "Tulip", "Tumble", "Twig", "Twinkle", "Ube", "Velvet", "Violet",
  "Waffles", "Walnut", "Wiggle", "Wiggles", "Willow", "Winnie", "Wisp", "Yuzu",
  "Zest", "Zinnia", "Zippy",
  // Extra cozy / classic dog nicknames
  "Ash", "Blaze", "Bolt", "Bongo", "Boomer", "Brie", "Cap", "Captain", "Chase",
  "Chewy", "Chipper", "Coco", "Comet", "Copper", "Crosby", "Dash", "Dexter",
  "Diesel", "Dodge", "Duke", "Dutch", "Echo", "Elvis", "Ember", "Felix",
  "Frankie", "Freddy", "Fudgepop", "Gunner", "Halo", "Hank", "Harley", "Hudson",
  "Hunter", "Ink", "Jasper", "Jax", "Kettle", "King", "Louie", "Lumen", "Marley",
  "Misty", "Murphy", "Nash", "Nova", "Odin", "Opal", "Otto", "Parker", "Pax",
  "Quill", "Ranger", "Rex", "Riley", "Rocky", "Rudy", "Rune", "Rusty", "Sable",
  "Samson", "Shadow", "Simba", "Tango", "Tank", "Thor", "Tucker", "Umber",
  "Vesper", "Winston", "Wren", "Xander", "Yarrow", "Zephyr", "Zeus", "Ziggy",
  "Abby", "Allie", "Annie", "Bella", "Callie", "Dixie", "Gigi", "Gracie", "Holly",
  "Izzy", "Josie", "Katie", "Lola", "Lucy", "Luna", "Maggie", "Millie", "Molly", "Nala",
  "Nina", "Penny", "Piper", "Ruby", "Sadie", "Stella", "Susie", "Zoe",
  // More park / food nicknames
  "Avocado", "Bageljack", "Baguette", "Baklava", "Banoffee", "Batter", "Bonbon",
  "Brisket", "Calpico", "Cannoli", "Clementine", "Crepe", "Croissant", "Crumbcake",
  "Dango", "Donut", "Dorito", "Eclair", "Fajita", "Fondue", "Gelato", "Guacamole",
  "Halva", "Hojicha", "Hotdog", "Hummus", "Icecube", "Icing", "Jambalaya", "Jello",
  "Kale", "Kimchi", "Lasagna", "Lokum", "Lychee", "Matcha", "Meatball", "Melona",
  "Mousse", "Mozzarella", "Nacho", "Nougat", "Omelette", "Onion", "Parfait",
  "Pavlova", "Persimmon", "Pocky", "Pretzel", "Queso", "Quince", "Ramen", "Ramune",
  "Rhubarb", "Samosa", "Sencha", "Strudel", "Syrup", "Taiyaki", "Takoyaki",
  "Tapioca", "Tart", "Tempura", "Tiramisu", "Trifle", "Udon", "Vanilla", "Waffle",
  "Wasabi", "Wonton", "Xigua", "Yogurt", "Ziti", "Zucchini",
  // Nature / park
  "Alder", "Aspen", "Birch", "Brook", "Canyon", "Cliff", "Coral", "Creek", "Delta",
  "Elm", "Fern", "Forest", "Gale", "Grove", "Harbor", "Iris", "Jade", "Lake",
  "Lark", "Moss", "Ocean", "Orchid", "Pine", "Pond", "Prairie", "Reed", "River",
  "Robin", "Shore", "Sparrow", "Storm", "Summit", "Tide", "Trail", "Vale", "Wave",
  "Woods", "Yew",
  // --- Everyday human first names ---
  "Aaron", "Abigail", "Adam", "Adrian", "Aiden", "Alan", "Albert", "Alejandro",
  "Alex", "Alexander", "Alexis", "Alice", "Alicia", "Allison", "Alyssa", "Amanda",
  "Amber", "Amelia", "Amy", "Andrea", "Andrew", "Angela", "Angelina", "Anna",
  "Anne", "Anthony", "Antonio", "April", "Ariana", "Arthur", "Ashley", "Audrey",
  "Austin", "Ava", "Barbara", "Beatrice", "Becky", "Ben", "Benjamin", "Beth",
  "Betty", "Beverly", "Bill", "Billy", "Blake", "Bobby", "Brad", "Bradley",
  "Brandon", "Brenda", "Brian", "Brittany", "Brooke", "Bruce", "Bryan", "Caleb",
  "Calvin", "Cameron", "Carl", "Carlos", "Carmen", "Carol", "Caroline", "Carolyn",
  "Carrie", "Casey", "Catherine", "Cathy", "Cecilia", "Chad", "Charles", "Charlie",
  "Charlotte", "Chelsea", "Cheryl", "Chloe", "Chris", "Christian", "Christina",
  "Christine", "Christopher", "Cindy", "Claire", "Clara", "Clarence", "Claude",
  "Claudia", "Clifford", "Clint", "Colin", "Colleen", "Connie", "Connor", "Corey",
  "Craig", "Crystal", "Curtis", "Cynthia", "Dale", "Dallas", "Damian", "Dan",
  "Dana", "Daniel", "Danielle", "Danny", "Darlene", "Dave", "David", "Dawn",
  "Dean", "Debbie", "Deborah", "Debra", "Denise", "Dennis", "Derek", "Diana",
  "Diane", "Diego", "Donald", "Donna", "Doris", "Dorothy", "Doug", "Douglas",
  "Drew", "Dustin", "Dylan", "Earl", "Eddie", "Edgar", "Edith", "Edward", "Edwin",
  "Eileen", "Elaine", "Elena", "Eli", "Elijah", "Elizabeth", "Ella", "Ellen",
  "Emily", "Emma", "Eric", "Erica", "Erik", "Erin", "Ernest", "Esther", "Ethan",
  "Eugene",
  "Eva", "Evan", "Evelyn", "Faith", "Faye", "Fernando", "Fiona", "Florence",
  "Floyd", "Frances", "Francis", "Frank", "Fred", "Frederick", "Gabriel", "Gail",
  "Gary", "Gavin", "Gene", "George", "Gerald", "Gina", "Glenn", "Gloria", "Gordon",
  "Grace", "Graham", "Grant", "Greg", "Gregory", "Guillermo", "Gwen", "Hailey",
  "Harold", "Harry", "Harvey", "Heather", "Hector", "Heidi", "Helen", "Henry",
  "Herbert", "Herman", "Hilary", "Hope", "Howard", "Hugh", "Hugo", "Ian", "Ida",
  "Irene", "Isaac", "Isabel", "Isabella", "Ivan", "Jack", "Jackie", "Jacob",
  "Jacqueline", "Jake", "James", "Jamie", "Jane", "Janet", "Janice", "Jared",
  "Jason", "Jay", "Jean", "Jeff", "Jeffrey", "Jen", "Jennifer", "Jenny", "Jeremy",
  "Jerry", "Jesse", "Jessica", "Jill", "Jim", "Jimmy", "Joan", "Joanna", "Joe",
  "Joel", "John", "Johnny", "Jon", "Jonathan", "Jordan", "Jose", "Joseph",
  "Josephine", "Josh", "Joshua", "Joyce", "Juan", "Judith", "Judy", "Julia",
  "Julian", "Julie", "Justin", "Kara", "Karen", "Kate", "Katherine", "Kathleen",
  "Kathryn", "Kathy", "Kay", "Kayla", "Keith", "Kelly", "Ken", "Kenneth", "Kevin",
  "Kim", "Kimberly", "Kirk", "Kristen", "Kristin", "Kristina", "Kurt", "Kyle",
  "Lance", "Larry", "Laura", "Lauren", "Lawrence", "Leah", "Lee", "Leo", "Leon",
  "Leonard", "Leroy", "Leslie", "Lester", "Levi", "Lewis", "Liam", "Lillian",
  "Linda", "Lindsay", "Lisa", "Lloyd", "Logan", "Lois", "Loretta", "Lorraine",
  "Louis", "Louise", "Lucas", "Luis", "Luke", "Lydia", "Lynn", "Mabel", "Madison",
  "Malcolm", "Manuel", "Marc", "Marcus", "Margaret", "Maria", "Marian", "Marie",
  "Marilyn", "Mario", "Marion", "Mark", "Martha", "Martin", "Marvin", "Mary",
  "Mason", "Matt", "Matthew", "Maureen", "Maurice", "Max", "Maya", "Megan",
  "Melanie", "Melinda", "Melissa", "Melvin", "Mia", "Michael", "Michelle", "Miguel",
  "Mike", "Mildred", "Monica", "Morgan", "Morris", "Nancy", "Naomi", "Natalie",
  "Nathan", "Nathaniel", "Neil", "Nelson", "Nicholas", "Nicole", "Noah", "Nora",
  "Norma", "Norman", "Oliver", "Olivia", "Oscar", "Owen", "Pamela", "Patricia",
  "Patrick", "Patty", "Paul", "Paula", "Pauline", "Pedro", "Peggy", "Peter",
  "Philip", "Phillip", "Phyllis", "Priscilla", "Quinn", "Rachel", "Ralph", "Ramon",
  "Randall", "Randy", "Ray", "Raymond", "Rebecca", "Regina", "Renee", "Rhonda",
  "Ricardo", "Richard", "Rick", "Ricky", "Rita", "Rob", "Robert", "Roberta",
  "Robin", "Roger", "Roland", "Ron", "Ronald", "Ronnie", "Rosa", "Rose", "Rosemary",
  "Ross", "Roy", "Ruben", "Russell", "Ruth", "Ryan", "Sally", "Sam", "Samantha",
  "Samuel", "Sandra", "Sara", "Sarah", "Scott", "Sean", "Sebastian", "Seth",
  "Shane", "Shannon", "Sharon", "Shawn", "Sheila", "Shelley", "Sherry", "Shirley",
  "Sidney", "Simon", "Sofia", "Sonia", "Sophia", "Stacey", "Stanley", "Stephanie",
  "Stephen", "Steve", "Steven", "Stuart", "Sue", "Susan", "Suzanne", "Sylvia",
  "Tamara", "Tammy", "Tara", "Taylor", "Teresa", "Terrance", "Terry", "Thelma",
  "Theodore", "Theresa", "Thomas", "Tiffany", "Tim", "Timothy", "Tina", "Todd",
  "Tom", "Tommy", "Tony", "Tracy", "Travis", "Trevor", "Troy", "Tyler", "Valerie",
  "Vanessa", "Vera", "Vernon", "Veronica", "Victor", "Victoria", "Vincent",
  "Virginia", "Vivian", "Wade", "Wallace", "Walter", "Wanda", "Warren", "Wayne",
  "Wendy", "Wesley", "Will", "William", "Willie", "Wyatt", "Yolanda", "Yvonne",
  "Zachary", "Zack",
] as const;

function isAllowedName(name: string): boolean {
  return !BANNED_KEYS.has(name.toLowerCase()) && name.trim().length > 0;
}

/** Cute snacky / park-puppy names + everyday human names for guest quick play, A-Z. */
export const DOG_NAMES: readonly string[] = [
  ...new Map(
    [...RAW_DOG_NAMES]
      .map((n) => n.trim())
      .filter(isAllowedName)
      .map((n) => [n.toLowerCase(), n] as const),
  ).values(),
].sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" }));

if (DOG_NAMES.length < 600) {
  throw new Error(`DOG_NAMES must contain at least 600 entries (got ${DOG_NAMES.length}).`);
}

const DOG_NAME_KEYS = new Map(DOG_NAMES.map((n) => [n.toLowerCase(), n]));

const FUR_COLORS = [
  0xe8d5b7, 0xc4a574, 0x8d6e63, 0x5d4037, 0xf5f5f5, 0x90a4ae, 0xffcc80, 0xbcaaa4,
  0xa1887f, 0x78909c, 0xd7ccc8, 0xffe0b2,
  // Extra seeds so more names land on different breed slots.
  0xffab91, 0xce93d8, 0x80cbc4, 0xffe082, 0xb0bec5, 0xd4a373,
];

/** Picks a random cozy dog name for the gate / guest join. */
export function randomName(): string {
  const pick = DOG_NAMES[Math.floor(Math.random() * DOG_NAMES.length)];
  return pick ?? "Pupper";
}

/**
 * Next name from a no-repeat cycle. Names already in `seen` are skipped until the
 * full list is exhausted, then the cycle resets. Never returns banned names.
 */
export function nextCycledName(
  seen: readonly string[],
  avoid?: string,
): { name: string; seen: string[] } {
  const avoidKey = avoid?.trim().toLowerCase() || "";
  const seenKeys = new Set(
    seen.map((n) => n.toLowerCase()).filter((n) => n && isAllowedName(n)),
  );

  let pool = DOG_NAMES.filter((n) => !seenKeys.has(n.toLowerCase()));
  let nextSeen = seen.filter((n) => isAllowedName(n));

  if (pool.length === 0) {
    pool = [...DOG_NAMES];
    nextSeen = [];
  }

  const preferred = avoidKey
    ? pool.filter((n) => n.toLowerCase() !== avoidKey)
    : pool;
  const candidates = preferred.length > 0 ? preferred : pool;
  const name = candidates[Math.floor(Math.random() * candidates.length)] ?? randomName();
  nextSeen = [...nextSeen, name];
  return { name, seen: nextSeen };
}

/**
 * Returns a new random name, preferring one different from `avoid` so the
 * shuffle button feels responsive. Prefer {@link nextCycledName} when
 * persisting per-player progress through the list.
 */
export function randomNameAvoiding(avoid?: string): string {
  return nextCycledName([], avoid).name;
}

export function sanitizeName(raw: string): string {
  const trimmed = raw.replace(/\s+/g, " ").trim().slice(0, NAME_MAX_LENGTH);
  if (!trimmed) {
    return randomName();
  }
  const canonical = DOG_NAME_KEYS.get(trimmed.toLowerCase());
  return canonical ?? randomName();
}

export function pickFurColor(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % FUR_COLORS.length;
  return FUR_COLORS[index] ?? 0xc4a574;
}
